import { z } from "zod";
import { MessageKind } from "./kinds.js";
/**
 * Sender corrections (architecture §6.7; WP-063). One correction per sender, for an address or a
 * whole domain, fixes that sender's past and future mail: `classify` puts it above every header.
 */
export const SenderMatch = z.enum(["address", "domain"]);
const DOMAIN = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9-]{2,63}$/;
/** An address or a domain as overrides store it: trimmed and lower case. */
export const SenderValue = z.string().trim().toLowerCase().min(3).max(320);
/** A sender as the actions take it; the value must suit its match. */
export const Sender = z
    .object({ match: SenderMatch, value: SenderValue })
    .superRefine((s, ctx) => {
    const ok = s.match === "address" ? z.string().email().safeParse(s.value).success : DOMAIN.test(s.value);
    if (!ok)
        ctx.addIssue({ code: "custom", path: ["value"], message: s.match === "address" ? "not an email address" : "not a domain" });
});
/**
 * `senderKinds/{id}` (id: `senderKindId`, one per owner, match and value). Written by the api
 * (`mail.setSenderKind`) and by sync for Claude's suggestions (`suggested: true`); clients read
 * their own and never write.
 */
export const SenderKindDoc = z.object({
    ownerUid: z.string().min(1),
    match: SenderMatch,
    value: SenderValue,
    kind: MessageKind,
    /** Claude's suggestion (§6.7), not yet confirmed by the owner. */
    suggested: z.boolean().default(false),
    createdAt: z.date(),
});
/** The override that applies to one sender: its address's, else its domain's. */
export const SenderOverride = z.object({ match: SenderMatch, kind: MessageKind, suggested: z.boolean() });
// ── actions ───────────────────────────────────────────────────────────────────────────────────
/** `mail.setSenderKind` (organize, undoable): a kind for the sender, or null to remove the override. */
export const MailSetSenderKindInput = z
    .object({ match: SenderMatch, value: SenderValue, kind: MessageKind.nullable() })
    .superRefine((s, ctx) => {
    const parsed = Sender.safeParse({ match: s.match, value: s.value });
    if (!parsed.success)
        for (const issue of parsed.error.issues)
            ctx.addIssue({ code: "custom", path: issue.path, message: issue.message });
});
export const MailSetSenderKindResult = z.object({
    /** Cached messages whose kind changed. */
    messages: z.number().int().nonnegative(),
    /** Conversations re-folded because of them. */
    threads: z.number().int().nonnegative(),
});
/** What `mail.setSenderKind` would change, stored as a plan for `actions.applyPlan` (§9). */
export const MailPlanSenderKindOutput = z.object({
    planId: z.string().min(1),
    /** Cached messages from the sender whose kind would change (a new reason alone does not count). */
    matched: z.number().int().nonnegative(),
    /** Conversations they belong to. */
    threads: z.number().int().nonnegative(),
    sample: z.array(z.string()),
    summary: z.string(),
    expiresAt: z.date(),
});
export const SenderKindView = z.object({
    id: z.string().min(1),
    match: SenderMatch,
    value: z.string(),
    kind: MessageKind,
    suggested: z.boolean(),
    createdAt: z.date(),
});
export const MailListSenderKindsOutput = z.object({ senders: z.array(SenderKindView) });
// ── Claude's suggestions (sync only) ─────────────────────────────────────────────────────────
/**
 * What the model may answer for a new sender: one of the four kinds or `unsure`. Anything else
 * is no answer. The answer only ever chooses a kind; it never chooses an action.
 */
export const SenderGuessAnswer = z.object({ kind: z.enum(["person", "notification", "newsletter", "receipt", "unsure"]) }).strict();
/**
 * `senderGuesses/{senderKindId(owner, address, value)}`, server-only: Claude was asked about this
 * sender once, and what it said (`invalid` when the reply did not parse).
 */
export const SenderGuessDoc = z.object({
    ownerUid: z.string().min(1),
    value: SenderValue,
    answer: z.enum(["person", "notification", "newsletter", "receipt", "unsure", "invalid"]),
    model: z.string(),
    at: z.date(),
});
//# sourceMappingURL=senders.js.map